import { Form, Input, ModalProps, Select } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import TextArea from "antd/es/input/TextArea";
import type { Category, ContentTopic } from "api/config/chalice-types";
import ModalWithBorders from "components/ModalWithBorders";
import { genericError } from "fixtures/globalConstants";
import { KeyboardEvent, useEffect, useState } from "react";
import { trackEvent } from "utils/eventTracking/trackEvents";

// Use the type imported from the hook
import type { TopicModalData } from "../hooks/useTopicsPageModals";

// Remove local definitions - use imported type
// export type TopicModalType = TopicModalData["type"];

// Form values structure for Topic Modal
interface TopicFormValues {
  title: string;
  description: string;
  categoryName?: string;
}

// Updated props for the combined component
type TopicFormModalProps = ModalProps & {
  modalData: TopicModalData;
  addTopic: (topic: ContentTopic) => void;
  updateTopic: (topic: ContentTopic, index: number) => void;
  onClose: () => void;
  categories?: Category[];
  existingTopics?: ContentTopic[]; // Added prop for uniqueness check
};

// Rename component function
const TopicFormModal = ({
  modalData,
  addTopic,
  updateTopic,
  onClose,
  categories = [],
  existingTopics = [], // Destructure with default
  ...modalProps // Pass remaining ModalProps (like open, but title is now managed externally)
}: TopicFormModalProps) => {
  const [form] = useForm<TopicFormValues>();
  const titleValue = useWatch("title", form); // Renamed to avoid conflict with title prop
  const [isLoading, setIsLoading] = useState(false);

  // Helper to get category name (moved from TopicModal)
  const getCategoryName = (
    categoryId: string | null | undefined
  ): string | undefined => {
    if (!categoryId) {
      // Return undefined for no category selected
      return undefined;
    }
    const category = categories.find((c) => c.id === categoryId);
    // Return undefined if not found or Uncategorized, so Select can show placeholder
    return category?.name === "Uncategorized" ? undefined : category?.name;
  };

  useEffect(() => {
    form.resetFields();
    // Set initial values based on modalData (logic from TopicModal)
    const { type, topic: currentTopic } = modalData;
    let initialValues: TopicFormValues = { title: "", description: "" };

    switch (type) {
      case "edit-topic":
        if (currentTopic) {
          initialValues = {
            title: currentTopic.title,
            description: currentTopic.body,
            categoryName:
              categories?.find((c) => c.id === currentTopic.category_id)
                ?.name ?? "Uncategorized",
          };
        }
        break;
      case "new-topic":
        // Pre-fill for new topic if partial data is provided
        initialValues = {
          title: currentTopic?.title ?? "",
          description: currentTopic?.body ?? "",
          categoryName: currentTopic?.category_id
            ? getCategoryName(currentTopic.category_id)
            : undefined,
        };
        break;
    }
    form.setFieldsValue(initialValues);
  }, [modalData, form, categories]); // Add categories to dependency array

  // Define keydown handler
  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    // Check for Cmd + Enter (Mac) or Ctrl + Enter (Windows/Linux)
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      // Prevent default behavior if needed (e.g., for TextArea)
      // event.preventDefault();
      form.submit();
    }
  };

  // Updated submission logic (from TopicModal's handleFormSubmit)
  const handleSave = async (formData: TopicFormValues) => {
    setIsLoading(true);
    try {
      const selectedCategory = categories.find(
        (c) => c.name === formData.categoryName
      );
      // Use null if category is undefined/cleared or name is "Uncategorized"
      const newTopicCategoryId = selectedCategory?.id || null;

      const newTopicData: ContentTopic = {
        title: formData.title,
        body: formData.description,
        enabled: true, // Assuming new/edited topics are always enabled
        category_id: newTopicCategoryId,
      };

      const { type, topic: currentTopic } = modalData;

      if (
        type === "edit-topic" &&
        currentTopic &&
        currentTopic.index !== undefined
      ) {
        await updateTopic(newTopicData, currentTopic.index);
        trackEvent("updated_topics", { action: "edit" });
      } else {
        await addTopic(newTopicData);
        trackEvent("updated_topics", { action: "add" }); // Track add event
      }
      onClose(); // Close modal on successful submission
    } catch (error) {
      genericError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalWithBorders
      // Title is now passed via props
      // title={modalData.modalType === "new-topic" ? "Add Topic" : "Edit Topic"}
      okButtonProps={{ loading: isLoading }}
      okText="Save"
      onOk={form.submit}
      onCancel={onClose}
      // Don't reset fields on cancel, only on close/re-open via useEffect
      // afterClose={() => form.resetFields()}
      {...modalProps}
    >
      <Form
        onFinish={handleSave}
        id="topic-modal-form" // Update ID
        layout="vertical"
        form={form}
        requiredMark={false}
        // Add keydown handler
        onKeyDown={handleKeyDown}
      >
        <Form.Item
          label="Title"
          name="title"
          rules={[
            {
              required: true,
              max: 50,
              message: "Title is required (max 50 chars).",
            },
            // Custom validator for uniqueness
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve(); // Already handled by required rule
                }
                const currentTitleLower = value.toLowerCase().trim();
                const isEditing = modalData.type === "edit-topic";
                const originalTitleLower =
                  isEditing && modalData.topic?.title
                    ? modalData.topic.title.toLowerCase().trim()
                    : "";

                const duplicateExists = existingTopics.some((topic) => {
                  const existingTitleLower = topic.title.toLowerCase().trim();
                  // Check if title matches AND it's not the topic being edited
                  return (
                    existingTitleLower === currentTitleLower &&
                    (!isEditing || existingTitleLower !== originalTitleLower)
                  );
                });

                if (duplicateExists) {
                  return Promise.reject(
                    new Error("A topic with this title already exists.")
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder={"Title of the topic"}
            suffix={
              <span className="text-antd-colorTextSecondary">
                {/* Use titleValue from useWatch */}
                {titleValue?.length ?? 0} / 50
              </span>
            }
          />
        </Form.Item>

        <Form.Item label="Category" name="categoryName">
          <Select
            placeholder="Select a category (optional)" // Update placeholder
            allowClear
            optionRender={(option) => {
              // Find the category object to get the color
              const category = categories.find(
                (cat) => cat.name === option.value
              );
              const color = category?.color ?? "#808080"; // Default gray if no color

              return (
                <span style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: color,
                      marginRight: "8px",
                    }}
                  />
                  {option.label}
                </span>
              );
            }}
          >
            {/* Manually map options to leverage optionRender */}
            {categories
              .filter((cat) => cat.name !== "Uncategorized") // Exclude "Uncategorized"
              .map((cat) => (
                <Select.Option key={cat.id} value={cat.name}>
                  {/* Label is rendered via optionRender */}
                  {cat.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          rules={[{ required: true, message: "Description is required." }]}
          label="Description"
          name="description"
        >
          <TextArea
            placeholder={
              "Couple sentence explanation of your topic. " +
              "This will be used as the starting point for a new post."
            }
            rows={3}
          />
        </Form.Item>
      </Form>
    </ModalWithBorders>
  );
};

// Update export
export default TopicFormModal;
